import React, { useState, useEffect } from 'react';
import { Input } from './Input';
import { MonkeyProps } from '../types/monkey.types';
import '../../../styles/components/search-bar.css';
import { SecurityUtils } from '../../../utils/security';
import { useAnalytics } from '../../../contexts/AnalyticsContext';

export interface SearchBarProps extends MonkeyProps {
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
  className?: string;
  debounceMs?: number;
  onSubmit?: () => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  query,
  onQueryChange,
  placeholder = "Search ...",
  className = '',
  onSubmit,
  debounceMs = 300,
  // Monkey props with default values
  showMonkey = false,
  monkeyVariant = 'singeOrange',
  monkeyType = 'walking',
  monkeyLookAt = 'right',
  monkeyPosition = 'position1'
}) => {
  const [localQuery, setLocalQuery] = useState(query);
  const analytics = useAnalytics();

  const handleInputChange = (value: string) => {
    //  SANITISATION AUTOMATIQUE
    const sanitizedValue = SecurityUtils.sanitizeInputLive(value);
    setLocalQuery(sanitizedValue);
  };

  // Effect for debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localQuery !== query) {
        onQueryChange(localQuery);
        
        // ===== ANALYTICS: Track search performed =====
        if (localQuery.trim().length > 0) {
          analytics.markSearchPerformed();
        }
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localQuery, debounceMs, onQueryChange, query]);

  // Update local state when query prop changes
  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.();
  };

  return (
    <form className={`search-bar-container ${className}`} onSubmit={handleSubmit}>
      <Input
        id="search-input"
        name="search"
        type="text"
        className="search-bar-input"
        value={localQuery}
        onChange={handleInputChange}
        placeholder={placeholder}
        icon="Search"
        showMonkey={showMonkey}
        monkeyVariant={monkeyVariant}
        monkeyType={monkeyType}
        monkeyLookAt={monkeyLookAt}
        monkeyPosition={monkeyPosition}
      />
    </form>
  );
};

// Add displayName to help with debugging
SearchBar.displayName = 'SearchBar';

export default SearchBar; 